/** 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 * Source: qubepro-core. Identity is central; effective product permissions are
 * resolved by Core for the current tenant, actor, company and application.
 */

export type ProductPermissionActor = {
  id: string;
  tenantId: string | null;
  userKind: 'INTERNAL' | 'EXTERNAL' | 'UNCLASSIFIED';
  membershipId?: string | null;
  isAdmin?: boolean;
  permissions: string[];
  membershipStatus?: string | null;
  membershipVersion?: number | null;
  tenantRole?: string | null;
  tenantAdminScope?: TenantAdminScope;
};

export type TenantAdminScope = {
  customerId: string; userId: string; membershipId: string; membershipVersion: string;
  companyId: string; appCode: string;
};

export function validTenantAdminScope(actor: ProductPermissionActor, value: unknown,
  scope: { appCode: string; companyId: string | null }): value is TenantAdminScope {
  const grant = value as TenantAdminScope | null;
  return !!grant && actor.userKind === 'INTERNAL' && actor.membershipStatus === 'ACTIVE'
    && ['TENANTOWNER', 'TENANTADMIN'].includes(actor.tenantRole ?? '')
    && /^[1-9]\d*$/.test(String(actor.membershipId ?? ''))
    && Number.isSafeInteger(actor.membershipVersion) && Number(actor.membershipVersion) >= 0
    && grant.customerId === actor.tenantId && grant.userId === actor.id
    && grant.membershipId === actor.membershipId && grant.membershipVersion === String(actor.membershipVersion)
    && grant.companyId === scope.companyId && /^[1-9]\d*$/.test(grant.companyId)
    && grant.appCode === scope.appCode && /^[A-Z][A-Z0-9_-]{1,39}$/.test(grant.appCode)
    && !['SYSTEM', 'PLATFORM', 'SYS_ADMIN', 'SYS_CONSOLE'].includes(grant.appCode);
}

export function tenantAdminAllows(actor: ProductPermissionActor | null | undefined, permission: string,
  scope: { appCode: string; companyId: string | null }): boolean {
  return !!actor && validTenantAdminScope(actor, actor.tenantAdminScope, scope)
    && /^[A-Za-z][A-Za-z0-9_-]*(?::[A-Za-z][A-Za-z0-9_-]*)+$/.test(permission)
    && (permission.split(':').length === 2 || permission.split(':')[0].toUpperCase() === scope.appCode)
    && !/^(system|platform|sys_admin|license|updates):/i.test(permission);
}

export type ProductPermissionScope = {
  appCode: string;
  companyId: string | null;
  applicationAuthMode: boolean;
};

export function needsProductPermissionContext(scope: ProductPermissionScope): boolean {
  return !scope.applicationAuthMode && scope.appCode !== 'CORE' && scope.companyId !== null;
}

/** Replaces the Core profile's codes, never unions them or infers a grant from
 * role IDs. The existing company boundary withholds the product until this
 * response has been verified; no permission payload is stored in the browser.
 */
export function applyProductPermissionContext<T extends ProductPermissionActor>(
  actor: T,
  response: unknown,
  scope: ProductPermissionScope,
): T {
  if (scope.applicationAuthMode) return actor.tenantAdminScope ? { ...actor, tenantAdminScope: undefined } : actor;
  if (scope.appCode === 'CORE') return !actor.tenantAdminScope ? actor : { ...actor, tenantAdminScope:
    validTenantAdminScope(actor, actor.tenantAdminScope, scope) ? actor.tenantAdminScope : undefined };
  const admin = actor.userKind === 'INTERNAL' && !actor.membershipId && actor.isAdmin === true;
  if (scope.companyId === null) {
    // Bootstrap discovers companies without projecting Core tenant grants into
    // another product. A separate server-proven central principal retains the
    // system-scope discovery it already had; this creates no new authority.
    return admin ? actor : { ...actor, tenantAdminScope: undefined, permissions: actor.userKind === 'INTERNAL' && !actor.membershipId
      ? actor.permissions.filter(code => code === 'system:admin') : [] };
  }
  const envelope = response as { success?: unknown; data?: unknown } | null;
  const context = envelope?.data as Record<string, unknown> | null;
  const permissions = context?.permissions;
  const tenantAdminScope = context?.tenantAdminScope;
  if (envelope?.success !== true || !context || context.contractVersion !== 1 || context.allowed !== true
    || !/^[A-Z][A-Z0-9_-]{1,39}$/.test(scope.appCode)
    || !/^(?:0|[1-9]\d*)$/.test(scope.companyId)
    || !/^[1-9]\d*$/.test(actor.id) || !/^[1-9]\d*$/.test(actor.tenantId ?? '')
    || context.userId !== actor.id || context.customerId !== actor.tenantId
    || context.companyId !== scope.companyId || context.appCode !== scope.appCode
    || !['INTERNAL', 'EXTERNAL'].includes(actor.userKind) || context.userKind !== actor.userKind
    || context.isAdmin !== admin
    || (tenantAdminScope !== undefined && !validTenantAdminScope(actor, tenantAdminScope, scope))
    || !Array.isArray(permissions) || permissions.length > 2048
    || permissions.some(code => typeof code !== 'string' || code.length > 240
      || (code === '*' ? !admin : !/^[A-Za-z][A-Za-z0-9_-]*(?::[A-Za-z][A-Za-z0-9_-]*)+$/.test(code)))) {
    throw new Error('Ürün ve firma erişim kapsamı doğrulanamadı.');
  }
  return { ...actor, permissions: [...new Set(permissions as string[])].sort(),
    tenantAdminScope: tenantAdminScope as TenantAdminScope | undefined };
}
